import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useCompanionStore } from '../../store/companion.store';
import ContextCard from '../cards/ContextCard';
import RecommendationCard from '../cards/RecommendationCard';

/* ── Shared panel content ─────────────────────────────── */
const PanelContent: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { contextItems } = useCompanionStore();

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle flex-shrink-0">
        <span className="text-2xs font-semibold text-text-muted uppercase tracking-wider">Conversation Context</span>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-text-muted hover:text-text-secondary hover:bg-surface-overlay transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand cursor-pointer"
          aria-label="Close context panel"
        >
          <X size={14} />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-3.5 py-3.5 space-y-3.5 overscroll-contain">
        {/* Context items */}
        {contextItems.length > 0 ? (
          <section>
            <p className="text-2xs font-medium text-text-muted uppercase tracking-wider mb-2">
              Active Context
            </p>
            <div className="space-y-1.5">
              {contextItems.map(item => (
                <ContextCard key={item.id} item={item} />
              ))}
            </div>
          </section>
        ) : (
          <section>
            <p className="text-2xs font-medium text-text-muted uppercase tracking-wider mb-2">
              Active Context
            </p>
            <div className="px-3.5 py-5 text-center rounded-xl border border-dashed border-border-subtle bg-surface-overlay/30">
              <p className="text-xs text-text-muted">Context and attached resources will appear here during conversations</p>
            </div>
          </section>
        )}

        {/* Recommendation */}
        <RecommendationCard
          title="Try Morning Briefing"
          description="Get a daily summary of your calendar, emails, and tasks."
          action={{
            label: 'Run workflow',
            onClick: () => {
              const send = (window as any).__MITRA_SEND__;
              if (send) send('Run my morning briefing');
            },
          }}
        />
      </div>
    </>
  );
};

/* ── Desktop Context Panel (smooth GPU transform + width transition) ─────────────── */
const DesktopContextPanel: React.FC = () => {
  const { contextPanel, toggleContextPanel, isMobile } = useCompanionStore();
  const open = contextPanel === 'open';

  // Don't render desktop version on mobile (it's controlled by the mobile sheet)
  if (isMobile) return null;

  return (
    <aside
      className={cn(
        "zone-context bg-surface-raised flex flex-col overflow-hidden transition-all duration-200 ease-in-out select-none",
        open
          ? "w-[280px] opacity-100 translate-x-0 border-l border-border-subtle pointer-events-auto"
          : "w-0 opacity-0 translate-x-4 border-l-0 pointer-events-none"
      )}
      aria-hidden={!open}
    >
      <div className="w-[280px] h-full flex flex-col overflow-hidden">
        <PanelContent onClose={toggleContextPanel} />
      </div>
    </aside>
  );
};

/* ── Mobile Context Sheet (overlay slide-in from right) ── */
const MobileContextSheet: React.FC = () => {
  const { mobileContextOpen, setMobileContextOpen, isMobile } = useCompanionStore();

  if (!isMobile) return null;

  return (
    <AnimatePresence>
      {mobileContextOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="mobile-overlay-backdrop"
            onClick={() => setMobileContextOpen(false)}
            aria-hidden="true"
          />

          {/* Sheet */}
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="mobile-context-sheet"
            role="dialog"
            aria-label="Context panel"
          >
            <PanelContent onClose={() => setMobileContextOpen(false)} />
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

/* ── Exported ContextPanel ─────────────────────────────── */
const ContextPanel: React.FC = () => (
  <>
    <DesktopContextPanel />
    <MobileContextSheet />
  </>
);

export default ContextPanel;
